import {
    GridAlignment,
    GridCellParams,
    GridColDef,
    GridFilterInputValue,
    GridFilterItem,
    GridFilterOperator
} from "@mui/x-data-grid-pro";
import { compareAsc, format, isAfter, isBefore, isEqual, parse } from "date-fns";
import React, { ReactNode } from "react";
import {APIGridDef} from "../model/User";
import {GridColumnDef, GridColumnTemplate} from "../components/grids/grid";

export const gridNilComparer = (v1: any, v2: any): number | null => {
    if (v1 == null && v2 != null) return -1;
    if (v2 == null && v1 != null) return 1;
    if (v1 == null && v2 == null) return 0;

    return null;
};

export const gridDateComparer = (value1: any, value2: any): number => {
    const nilResult = gridNilComparer(value1, value2);
    if (nilResult !== null) {
        return nilResult;
    }

    if ((typeof value1 === "string") && (typeof value2 === "string")) {
        const v1 = parse(value1, "yyyy-MM-dd", new Date())
        const v2 = parse(value2, "yyyy-MM-dd", new Date())

        return compareAsc(v1, v2);
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (value1! > value2!) return 1;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (value1! < value2!) return -1;
    return 0;
};

export const gridDateOperators: GridFilterOperator[] = [
    {
        value: "is",
        getApplyFilterFn: (filterItem: GridFilterItem) => {
            return (params): boolean => {
                if (typeof params.value === "string") {
                    const v1 = parse(params.value, "yyyy-MM-dd", new Date())
                    const v2 = parse(filterItem.value, "yyyy-MM-dd", new Date())

                    return isEqual(v1, v2);
                }

                return false;
            }
        },
        InputComponent: GridFilterInputValue,
        InputComponentProps: { type: "date" },
    },
    {
        value: "not",
        getApplyFilterFn: (filterItem: GridFilterItem) => {
            return (params): boolean => {
                if (typeof params.value === "string") {
                    const v1 = parse(params.value, "yyyy-MM-dd", new Date())
                    const v2 = parse(filterItem.value, "yyyy-MM-dd", new Date())

                    return !isEqual(v1, v2);
                }

                return false;
            }
        },
        InputComponent: GridFilterInputValue,
        InputComponentProps: { type: 'date' },
    },
    {
        value: "after",
        getApplyFilterFn: (filterItem: GridFilterItem) => {
            return (params): boolean => {
                if (typeof params.value === "string") {
                    const v1 = parse(params.value, "yyyy-MM-dd", new Date())
                    const v2 = parse(filterItem.value, "yyyy-MM-dd", new Date())

                    return isAfter(v1, v2);
                }

                return false;
            }
        },
        InputComponent: GridFilterInputValue,
        InputComponentProps: { type: "date" },
    },
    {
        value: "onOrAfter",
        getApplyFilterFn: (filterItem: GridFilterItem) => {
            return (params): boolean => {
                if (typeof params.value === "string") {
                    const v1 = parse(params.value, "yyyy-MM-dd", new Date())
                    const v2 = parse(filterItem.value, "yyyy-MM-dd", new Date())

                    return isEqual(v1, v2) || isAfter(v1, v2);
                }

                return false;
            }
        },
        InputComponent: GridFilterInputValue,
        InputComponentProps: { type: "date" },
    },
    {
        value: "before",
        getApplyFilterFn: (filterItem: GridFilterItem) => {
            return (params): boolean => {
                if (typeof params.value === "string") {
                    const v1 = parse(params.value, "yyyy-MM-dd", new Date())
                    const v2 = parse(filterItem.value, "yyyy-MM-dd", new Date())

                    return isBefore(v1, v2);
                }

                return false;
            }
        },
        InputComponent: GridFilterInputValue,
        InputComponentProps: { type: "date" },
    },
    {
        value: "onOrBefore",
        getApplyFilterFn: (filterItem: GridFilterItem) => {
            return (params): boolean => {
                if (typeof params.value === "string") {
                    const v1 = parse(params.value, "yyyy-MM-dd", new Date())
                    const v2 = parse(filterItem.value, "yyyy-MM-dd", new Date())

                    return isEqual(v1, v2) || isBefore(v1, v2);
                }

                return false;
            }
        },
        InputComponent: GridFilterInputValue,
        InputComponentProps: { type: "date" },
    },
];

export const gridDateRenderer = (params: GridCellParams): ReactNode => {
    if (params.value && params.value !== "") {
        try {
            const date = parse(params.value as string, "yyyy-MM-dd", new Date())
            const dateStr = format(date, "dd/MM/yyyy");

            return (<div>{dateStr}</div>);
        } catch (e) {
            console.warn(`unable to format field ${params.field}: `, e)
            return (<div />);
        }
    } else {
        return (<div />);
    }
};

export const gridDateTimeRenderer = (params: GridCellParams): ReactNode => {
    if (params.value && params.value !== "") {
        try {
            const date = parse(params.value as string, "yyyy-MM-dd'T'HH:mm:ss", new Date())
            const dateTimeStr = format(date, "dd/MM/yyyy HH:mm:ss");

            return (<div>{dateTimeStr}</div>);
        } catch (e) {
            console.warn(`unable to format field ${params.field}: `, e)
            return (<div />);
        }
    } else {
        return (<div />);
    }
};

export const gridAlignCentre: GridAlignment = "center";

export const convertAPIDef = (def: APIGridDef, baseDef: GridColumnDef[]): GridColumnDef[] => {
    if (def === undefined || def.columns === undefined || def.columns.length === 0) {
        throw new Error("malformed grid definition");
    }

    const newDef:GridColumnDef[] = [
        ...baseDef
    ];

    def.columns.forEach((column) => {
        if (column.fieldId === undefined || column.fieldId.trim().length === 0) {
            console.error(`malformed column fieldId: ${column.fieldId}`);
            return;
        }

        let width = 0;
        if (column.width !== undefined) {
            try {
                width = parseInt(column.width);
            } catch (e) {
                console.error(`unable to parse width for column ${column.fieldId}`)
            }
        }

        newDef.push({
            field: column.fieldId,
            headerName: column.tag !== undefined && column.tag.trim().length > 0 ? column.tag : column.fieldId,
            ...width > 0 && {width: width}
        })
    });

    return newDef;
}

const genericColumnTemplate:GridColumnTemplate = {
    genericString: {
        field: 'genericString',
        headerName: 'Text',
        width: 180,
        type: 'string',
    },
    genericNumber: {
        field: 'genericNumber',
        headerName: 'Number',
        width: 180,
        type: 'number',
    },
    genericDate: {
        field: 'genericDate',
        headerName: 'Date',
        width: 180,
        type: 'date',
        sortComparator: gridDateComparer,
        filterOperators: gridDateOperators,
        renderCell: gridDateRenderer
    },
};

export const buildCustomGrid = (columnDef: GridColumnDef[], columnTemplate:GridColumnTemplate):GridColDef[] => {
    const ret: GridColDef[] = [];

    columnDef.forEach((item) => {
        const {field, type = "string", headerName, width} = item;
        if (columnTemplate[field] !== undefined) {
            ret.push({
                ...columnTemplate[item.field],
                headerName: headerName !== undefined ? headerName : columnTemplate[field].headerName,
                width: width !== undefined ? width : columnTemplate[field].width,
            })
        } else {
            if (type === "date") {
                ret.push({
                    ...genericColumnTemplate["genericDate"],
                    field: field,
                    headerName: headerName !== undefined ? headerName : genericColumnTemplate["genericDate"].headerName,
                    width: width !== undefined ? width : genericColumnTemplate["genericDate"].width,
                });
            } else if (type === "number") {
                ret.push({
                    ...genericColumnTemplate["genericNumber"],
                    field: field,
                    headerName: headerName !== undefined ? headerName : genericColumnTemplate["genericNumber"].headerName,
                    width: width !== undefined ? width : genericColumnTemplate["genericNumber"].width,
                });
            } else {
                ret.push({
                    ...genericColumnTemplate["genericString"],
                    field: field,
                    headerName: headerName !== undefined ? headerName : genericColumnTemplate["genericString"].headerName,
                    width: width !== undefined ? width : genericColumnTemplate["genericString"].width,
                });
            }
        }
    });

    return ret;
}
